import api from '../api';
import i18n from '../../i18n';
import Swal from 'sweetalert2';

export const Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000,
});
const API_URL = `${process.env.REACT_APP_API_DOTNET}/api/v1/authentication`;

/**
 * Decodes a base64url-encoded string.
 * JWT tokens use base64url encoding (RFC 7519) which uses '-' and '_' instead of '+' and '/'.
 * JavaScript's atob() only accepts standard base64, so we need to convert first.
 * @param {string} base64url - The base64url-encoded string to decode
 * @returns {string} The decoded string
 */
function decodeBase64Url(base64url) {
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) {
        base64 += '='.repeat(4 - padding);
    }
    return atob(base64);
}

class AuthService {
    /**
     * @param {string} username
     * @param {string} password
     * @param {string} target
     */
    async login(username, password, target) {
        let success = false;
        /** @type {import('axios').AxiosResponse<{ success: boolean, token: string }>} */
        await api.post(`${API_URL}/Login`, { username, password })
            .then(response => {
                this.setAuthToken(response.data.token, target);
                success = true;
            })
            .catch(e => {
                const error = i18n.t('common:Login.genericError');
                Toast.fire({
                    icon: 'error',
                    title: error,
                    timer: 3000
                });
            });
        return success;
    }

    /**
     * @param {string} ssoToken
     * @param {string} target
     */
    async ssoLogin(ssoToken, target) {
        let success = false;
        /** @type {import('axios').AxiosResponse<{ success: boolean, token: string }>} */
        await api.post(`${API_URL}/Sso`, { ssoToken })
            .then(response => {
                this.setAuthToken(response.data.token, target);
                success = true;
            })
            .catch(e => {
                const error = i18n.t('common:Login.genericError');
                Toast.fire({
                    icon: 'error',
                    title: error,
                    timer: 3000
                });
                setTimeout(() => {
                    var redirectUrl = sessionStorage.getItem('logoutRedirectUrl');
                    window.location.href = redirectUrl || `${window.location.pathname}?target=${target}`;
                }, 3000)
            });
        return success;
    }

    refreshToken() {
        const jwt = sessionStorage.getItem('authToken');
        if (jwt) {
            /** @type {import('axios').AxiosResponse<{ success: boolean, token: string }>} */
            api.post(`${API_URL}/Refresh`, { token: jwt })
                .then(response => {
                    this.setAuthToken(response.data.token);
                })
                .catch(e => {
                    console.log(e);
                    const error = i18n.t('common:Login.genericError');
                    Toast.fire({
                        icon: 'error',
                        title: error,
                        timer: 3000
                    });
                    this.logout();
                });
        }
    }

    checkTokenExpiration = () => {
        const jwtLifeSpan = sessionStorage.getItem('jwtLifespan');
        if (jwtLifeSpan) {
            const lifeSpan = JSON.parse(jwtLifeSpan);

            if (new Date().getTime() > lifeSpan.Expiration * 1000) {
                this.logout();
                return true;
            }
        } else {
            return true;
        }
        return false;
    }

    /**
     * @param {string} license
     * @returns {boolean}
     */
    validateLicense = (license) => {
        const jwtUserInfo = sessionStorage.getItem('jwtUserInfo');
        if (jwtUserInfo) {
            const userInfo = JSON.parse(jwtUserInfo);
            return userInfo.Licenses.includes(license);
        }
        return false;
    }

    /**
     * @param {string} authToken
     */
    setAuthToken = (authToken, target) => {
        const tokenSections = authToken.split('.');
        const tokenContent = decodeBase64Url(tokenSections[1]);
        const content = JSON.parse(tokenContent);

        // Decode actort UTF-8 bytes to proper string (fixes accented characters)
        const actortBytes = Uint8Array.from(content.actort, (c) => c.charCodeAt(0));
        const actort = new TextDecoder('utf-8').decode(actortBytes);

        /** @type {JsonWebToken.ITokenLifeSpan} */
        const tokenInfo = {
            IssuedAt: content.iat,
            NotBefore: content.nbf,
            Expiration: content.exp,
        };

        const jwtUserInfo = JSON.parse(actort);
        const { FirstName, LastName, PersonNumber, UserId, FullName, PrimaryJob, FirstDayOfWeek, HrisUrl } = jwtUserInfo;
        const firstNameInitial = FirstName ? FirstName.charAt(0) : "";
        const lastNameInitial = LastName ? LastName.charAt(0) : "";
        const userInitials = `${firstNameInitial}${lastNameInitial}`;

        const authTokenWithScheme = `Bearer ${authToken}`;
        sessionStorage.setItem('authToken', authTokenWithScheme);
        sessionStorage.setItem('jwtUserInfo', actort);
        sessionStorage.setItem('jwtLifespan', JSON.stringify(tokenInfo));

        sessionStorage.setItem('userInitials', userInitials);
        sessionStorage.setItem('PersonNumber', PersonNumber);
        sessionStorage.setItem('UserId', UserId);
        sessionStorage.setItem('FullName', FullName);
        sessionStorage.setItem('PrimaryJob', PrimaryJob);
        sessionStorage.setItem('WeekStartDay', FirstDayOfWeek + 1);

        sessionStorage.setItem('appTarget', target);
        sessionStorage.setItem('logoutRedirectUrl', HrisUrl);
    }

    logout = () => {
        var redirectUrl = sessionStorage.getItem('logoutRedirectUrl');
        var target = sessionStorage.getItem('appTarget');

        sessionStorage.removeItem('logoutRedirectUrl');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('jwtUserInfo');
        sessionStorage.removeItem('jwtLifespan');
        sessionStorage.removeItem('userInitials');
        sessionStorage.removeItem('PersonNumber');
        sessionStorage.removeItem('UserId');
        sessionStorage.removeItem('FullName');
        sessionStorage.removeItem('PrimaryJob');
        sessionStorage.removeItem('WeekStartDay');
        window.location.href = redirectUrl || `${window.location.pathname}?target=${target}`;
    }

    isLoggedIn = () => {
        const authToken = sessionStorage.getItem('jwtUserInfo');
        if (!authToken) {
            return false;
        }

        const expired = this.checkTokenExpiration();
        if (expired) {
            return false;
        }

        return true;
    }

    // TODO Seb - 2022-05-04 - Add this info to the JWT (https://dev.azure.com/in-rgy/ShiftBidding/_sprints/taskboard/ShiftBidding%20Team/ShiftBidding/It%C3%A9ration%2028?workitem=9409)
    isManager = () => window.location.href.toLowerCase().split('/')[3] === 'manager'
}

export default (new AuthService());

export const getAuthHeader = () => ({
    headers: {
        Authorization: sessionStorage.getItem('authToken') ?? '',
    }
});
